/*!
 * opus-cart-drawer-a11y.js
 * ------------------------------------------------------------------
 * Adds accessible names to controls in the Opus Cart Drawer + Cart
 * Upsell Shopify app, which ships several interactive elements with no
 * accessible name at all. Screen readers announce them as bare
 * "button" / "edit text" with no indication of what they act on.
 *
 * WCAG 2.1: 4.1.2 Name, Role, Value (A) and 3.3.2 Labels or
 * Instructions (A).
 *
 * The app renders into an OPEN shadow root on #opus-shadow-container,
 * so its internals are reachable from the page. Nothing here changes
 * layout, behaviour, roles or event handling. It only sets aria-label
 * and repairs placeholder alt text, so it is safe to leave installed
 * after the vendor ships their own fix (the guard below skips any
 * element that already has a name).
 *
 * Remove this file once Opus labels these controls upstream.
 *
 * Verified against shop.staugustinedistillery.com, 2026-08-10.
 * Connected Systems.
 */
(function () {
  'use strict';

  var HOST_SELECTOR = '#opus-shadow-container';
  var POLL_MS = 200;
  var POLL_LIMIT = 100; // ~20s, then give up quietly

  function text(el) {
    return el ? (el.textContent || '').trim().replace(/\s+/g, ' ') : '';
  }

  // Only ever ADD a name. Never overwrite one the app already provides,
  // so this becomes inert the day Opus fixes it upstream.
  function nameIfUnnamed(el, value) {
    if (!el || !value) return;
    var existing = (el.getAttribute('aria-label') || '').trim();
    if (existing && existing !== value && !el.hasAttribute('data-cs-a11y')) return;
    if (existing === value) return;
    el.setAttribute('aria-label', value);
    el.setAttribute('data-cs-a11y', '1');
  }

  function patch(root) {
    // 1. Upsell "Add to cart" buttons.
    //    Ship with no aria-label, no title, no text, no labelledby and an
    //    unlabelled inline SVG, so every one announces as just "button".
    root.querySelectorAll('.cd-carousel-product').forEach(function (card) {
      var name = text(card.querySelector('.cd-product-title'));
      var btn = card.querySelector('.cd-buy-button');
      if (btn && name) nameIfUnnamed(btn, 'Add ' + name + ' to cart');
    });

    // 2. Cart line items: remove control, quantity field, thumbnail alt.
    root.querySelectorAll('.cd-product-item').forEach(function (item) {
      var name = text(item.querySelector('.cd-product-item-title'));
      if (!name) return;

      // Remove control ships as "Remove item" on every row, so with more
      // than one item in the cart they are indistinguishable.
      var del = item.querySelector('.cd-product-item-delete');
      if (del) {
        var current = (del.getAttribute('aria-label') || '').trim();
        if (!current || current === 'Remove item' || del.hasAttribute('data-cs-a11y')) {
          del.setAttribute('aria-label', 'Remove ' + name + ' from cart');
          del.setAttribute('data-cs-a11y', '1');
        }
      }

      // Quantity input has no label, no aria-label and no id.
      var qty = item.querySelector('.cd-number-input-field');
      if (qty) nameIfUnnamed(qty, 'Quantity for ' + name);

      // Thumbnail alt is the literal string "Placeholder Product".
      var img = item.querySelector('img');
      if (img) {
        var alt = (img.getAttribute('alt') || '').trim();
        if (!alt || /^placeholder product$/i.test(alt)) img.setAttribute('alt', name);
      }
    });

    // 3. Special instructions textarea ships with no accessible name.
    root.querySelectorAll('.cd-special-instructions-textarea').forEach(function (ta) {
      nameIfUnnamed(ta, 'Special instructions for your order');
    });
  }

  function attach() {
    var host = document.querySelector(HOST_SELECTOR);
    if (!host || !host.shadowRoot) return false;

    var root = host.shadowRoot;
    var timer = null;

    patch(root);

    // The drawer re-renders on open, on cart change and as the upsell
    // carousel loads, so a one-shot pass is not enough. Observing
    // childList only (not attributes) means our own aria-label writes
    // cannot retrigger the observer.
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () { patch(root); }, 60);
    }).observe(root, { childList: true, subtree: true });

    return true;
  }

  if (!attach()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (attach() || ++tries > POLL_LIMIT) clearInterval(poll);
    }, POLL_MS);
  }
})();
